import { Section } from "@/components/Global/Section/Section";
import { useAppContext } from "@/context/AppContext";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import { useRouter } from "next/router";
import React, { FormEvent, useEffect, useState } from "react";
import styles from "./Profile.module.scss";
import { FormErrorState, Input } from "@/components/Global/Input/Input";
import { SubmitButton } from "@/components/Global/Button/Button";
import { customSanitize } from "@/utils/customSanitize";
import { deleteUser, updateUser } from "@/utils/serviceAPI/user";
import { useModal } from "@/components/_primitives/Modal/ModalRoot/ModalRoot";
import ProfilePicture, { PreviewState } from "./ProfilePicture";
import { uploadImage, removeImage } from "@/utils/serviceAPI/image";
import { safeParseSchema, userUpdateSchema } from "@/utils/validations";
import { UserType } from "@/types/baseTypes";
import { Loader } from "@/components/Loader/Loader";

function Profile() {
  const { user, setFormModalText, setFormModalType } = useAppContext();
  const { getLabel } = useGlobalContext();
  const [errors, setErrors] = useState<FormErrorState>();
  const [loading, setLoading] = React.useState(false);
  const router = useRouter();
  const { setOpen } = useModal();
  const [preview, setPreview] = useState<PreviewState>({
    path: undefined,
    file: undefined,
    delete: false,
  });
  // redirect without delay to /login if user is not logged in
  useEffect(() => {
    if (!user) {
      router.push("/login");
      setLoading(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const { firstName, lastName, email } = user || {};
  async function handleDelete() {
    try {
      await deleteUser();
      setFormModalType("delete");
      setOpen(true);
    } catch (err) {
      setOpen(true);
      setFormModalText(
        err instanceof Error
          ? err.message
          : "An error occurred while deleting the account.",
      );
    }
  }
  const validationMessages = {
    email_invalid: getLabel("email_invalid"),
    required_field: getLabel("required_field"),
  };
  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const formProps = Object.fromEntries(formData);
    const { data, flattenedErrors } = safeParseSchema(
      userUpdateSchema(validationMessages),
      formProps,
    );
    if (data) {
      // UPDATE PROFILE INFO
      let imagePath = user && user.profilePicture;
      const updatedData = data as UserType;
      // ? Handle Image Upload
      if (preview.file) {
        const file = preview.file;
        const uploadImage_result = await uploadImage(file);
        if (uploadImage_result instanceof Error) {
          return;
        } else {
          imagePath = uploadImage_result.path;
        }
      }

      //? Handle Image Removal
      if (preview.delete && user && user.profilePicture) {
        await removeImage(user.profilePicture);
      }

      // only update names or fav food
      if (user) {
        const updatedUser = {
          firstName: updatedData.firstName,
          lastName: updatedData.lastName,
          id: user.id,
          profilePicture: imagePath,
        };
        const res = await updateUser({
          ...updatedUser,
          email: user.email,
          favouriteFood: "",
          userLocale: user.userLocale || "de",
        });
        if (res) {
          sessionStorage.removeItem("userData");
          location.reload();
          setLoading(true);
        }
      }
    } else {
      setErrors(flattenedErrors);
    }
  }
  return (
    <Section
      columnsClassName="is-centered is-multiline"
      className={`${styles.profile}`}
    >
      <div className="column is-10-tablet is-5-desktop is-4-fullhd">
        <ProfilePicture user={user} preview={preview} setPreview={setPreview} />
      </div>
      <div className="column is-10-tablet is-6-desktop is-offset-1-desktop is-7-fullhd is-offset-1-fullhd">
        <form onSubmit={handleSubmit} autoComplete="on">
          <h1 className="h1-pages">
            {firstName} {lastName}
          </h1>
          <p className={`login-area-headline`} role="heading" aria-level={2}>
            {getLabel("login_form_label_personal")}
          </p>
          <Input
            inputType="text"
            id="firstName"
            name="firstName"
            label={getLabel("login_form_label_first_name")}
            placeholder={getLabel("login_form_placeholder_first_name")}
            errors={errors}
            {...(user && user.firstName
              ? { defaultValue: user.firstName }
              : {})}
          ></Input>
          <Input
            inputType="text"
            id={"lastName"}
            name="lastName"
            label={getLabel("login_form_label_last_name")}
            placeholder={getLabel("login_form_placeholder_last_name")}
            errors={errors}
            {...(user && user.lastName ? { defaultValue: user.lastName } : {})}
          ></Input>
          <p className={`login-area-headline`} role="heading" aria-level={2}>
            {getLabel("login_form_label_email")}
          </p>
          <p>{email}</p>
          <SubmitButton>{getLabel("save_changes")}</SubmitButton>
        </form>
        <section className={`${styles["profile-delete"]}`}>
          <header>
            <p className={`login-area-headline`} role="heading" aria-level={2}>
              {getLabel("delete_account_headline")}
            </p>
            <p
              dangerouslySetInnerHTML={{
                __html: customSanitize(getLabel("delete_account_content")),
              }}
            />
          </header>
          <footer>
            <button
              onClick={handleDelete}
              className={`button-reset ${styles["delete-button"]}`}
            >
              {getLabel("delete_account_button")}
            </button>
          </footer>
        </section>
      </div>
      {loading && <Loader />}
    </Section>
  );
}

export default Profile;
